import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';

interface UserRole {
    id?: string;
    userId: string;
    email: string;
    displayName: string;
    isAdmin: boolean;
    canManageCourses: boolean;
    canViewPosts: boolean;
    canCreatePosts: boolean;
    canManageUnits: boolean;
    allowedSchools: string[];
}

interface School {
    id: string;
    name: string;
}

export const AdminUserManagement = () => {
    const [currentUser] = useAuthState(auth);
    const [userRoles, setUserRoles] = useState<UserRole[]>([]);
    const [schools, setSchools] = useState<School[]>([]);
    const [currentUserRole, setCurrentUserRole] = useState<UserRole | null>(null);
    const [loading, setLoading] = useState(true);
    const [searchUID, setSearchUID] = useState('');
    const [updateTimeouts, setUpdateTimeouts] = useState<{[key: string]: NodeJS.Timeout}>({});

    const debouncedUpdate = (roleId: string, updates: Partial<UserRole>) => {
        // Clear existing timeout for this field
        const timeoutKey = `${roleId}-${Object.keys(updates)[0]}`;
        if (updateTimeouts[timeoutKey]) {
            clearTimeout(updateTimeouts[timeoutKey]);
        }

        // Update state immediately for responsive UI
        setUserRoles(prev => prev.map(role => 
            role.id === roleId ? { ...role, ...updates } : role
        ));

        // Set new timeout for Firebase update
        const newTimeout = setTimeout(async () => {
            try {
                await updateDoc(doc(db, "userRoles", roleId), updates);
                console.log("Updated:", updates); // Silent update
            } catch (error) {
                console.error("Error updating user role:", error);
                alert("Error updating user role");
            }
        }, 1000); // Wait 1 second after user stops typing

        setUpdateTimeouts(prev => ({
            ...prev,
            [timeoutKey]: newTimeout
        }));
    };

    // Check if current user is admin
    useEffect(() => {
        const checkAdminStatus = async () => {
            if (!currentUser?.uid) return;
            
            try {
                const roleQuery = query(
                    collection(db, "userRoles"), 
                    where("userId", "==", currentUser.uid)
                );
                const roleSnapshot = await getDocs(roleQuery);
                
                if (!roleSnapshot.empty) {
                    const role = roleSnapshot.docs[0].data() as UserRole;
                    setCurrentUserRole(role);
                }
            } catch (error) {
                console.error("Error checking admin status:", error);
            }
        };

        checkAdminStatus();
    }, [currentUser]);

    // Fetch all user roles and schools
    useEffect(() => {
        const fetchData = async () => {
            if (!currentUserRole?.isAdmin) return;

            try {
                // Fetch user roles
                const rolesSnapshot = await getDocs(collection(db, "userRoles"));
                const roles = rolesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as UserRole[];
                setUserRoles(roles);

                // Fetch schools
                const schoolsSnapshot = await getDocs(collection(db, "schools"));
                const schoolsData = schoolsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as School[];
                setSchools(schoolsData);

                setLoading(false);
            } catch (error) {
                console.error("Error fetching data:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUserRole]);

    const createUserRole = async (uid: string, displayName: string = "New User") => {
        if (!uid.trim()) {
            alert("Please enter a UID");
            return;
        }

        // Check if user already exists
        if (userRoles.find(role => role.userId === uid.trim())) {
            alert("User with this UID already exists!");
            return;
        }

        try {
            const newRoleRef = doc(collection(db, "userRoles"));
            const newRole = {
                userId: uid.trim(),
                email: "unknown@example.com", // Will be updated when we have more info
                displayName: displayName,
                isAdmin: false,
                canManageCourses: false,
                canViewPosts: false,
                canCreatePosts: false,
                canManageUnits: false,
                allowedSchools: []
            };
            
            await setDoc(newRoleRef, newRole);

            // Add to state directly
            setUserRoles(prev => [...prev, { id: newRoleRef.id, ...newRole }]);
            
            setSearchUID('');
            alert("User role created successfully! Set their permissions using the checkboxes.");
        } catch (error) {
            console.error("Error creating user role:", error);
            alert("Error creating user role");
        }
    };

    const updateUserRole = async (roleId: string, updates: Partial<UserRole>, showAlert: boolean = true) => {
        try {
            await updateDoc(doc(db, "userRoles", roleId), updates);
            
            // Update state directly instead of re-fetching
            setUserRoles(prev => prev.map(role => 
                role.id === roleId ? { ...role, ...updates } : role
            ));
            
            if (showAlert) {
                alert("User role updated successfully!");
            }
        } catch (error) {
            console.error("Error updating user role:", error);
            alert("Error updating user role");
        }
    };

    const deleteUserRole = async (roleId: string) => {
        if (!window.confirm("Are you sure you want to delete this user role?")) return;

        try {
            await deleteDoc(doc(db, "userRoles", roleId));
            
            // Remove from state directly
            setUserRoles(prev => prev.filter(role => role.id !== roleId));
            
            alert("User role deleted successfully!");
        } catch (error) {
            console.error("Error deleting user role:", error);
            alert("Error deleting user role");
        }
    };

    const toggleSchoolAccess = async (role: UserRole, schoolId: string) => {
        if (!role.id) return;

        const currentSchools = role.allowedSchools || [];
        const updatedSchools = currentSchools.includes(schoolId)
            ? currentSchools.filter(id => id !== schoolId)
            : [...currentSchools, schoolId];

        await updateUserRole(role.id, { allowedSchools: updatedSchools }, true);
    };

    // Show access denied if not admin
    if (!currentUserRole?.isAdmin) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <h2 style={{ color: 'white' }}>Access Denied</h2>
                <p style={{ color: 'white' }}>You must be an administrator to access this page.</p>
            </div>
        );
    }

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px', color: 'white' }}>Loading...</div>;
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto', color: 'white' }}>
            <h1 style={{ color: 'white', marginBottom: '30px' }}>User Management</h1>

            {/* User Search Section */}
            <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #555', borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.3)' }}>
                <h3 style={{ color: 'white', marginBottom: '15px' }}>Add New User</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
                    <input
                        type="text"
                        placeholder="Paste user UID here"
                        value={searchUID}
                        onChange={(e) => setSearchUID(e.target.value)}
                        style={{ 
                            flex: 1, 
                            padding: '10px', 
                            border: '1px solid #555', 
                            borderRadius: '4px',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            fontFamily: 'monospace',
                            fontSize: '12px'
                        }}
                    />
                    <button
                        onClick={() => createUserRole(searchUID, "New User")}
                        disabled={!searchUID.trim()}
                        style={{ 
                            padding: '10px 20px', 
                            backgroundColor: '#28a745', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Create User Role
                    </button>
                </div>
                
                <div style={{ padding: '15px', border: '1px solid #666', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                    <h4 style={{ color: 'white', marginTop: 0 }}>How to Add Users:</h4>
                    <ol style={{ color: 'white', marginBottom: 0 }}>
                        <li>Send user this link: <code style={{ backgroundColor: 'rgba(0,0,0,0.5)', padding: '2px 4px' }}>{window.location.origin}/get-uid</code></li>
                        <li>User visits link while logged in to copy their UID</li>
                        <li>Paste the UID in the field above and click "Create User Role"</li>
                        <li>Set their permissions using the checkboxes in the table below</li>
                    </ol>
                </div>
            </div>

            {/* Users Table */}
            <div style={{ overflowX: 'auto', border: '1px solid #555', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'left', color: 'white' }}>Email</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'left', color: 'white' }}>Display Name</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'left', color: 'white' }}>User ID (UID)</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>Admin</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>Manage Courses</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>View Posts</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>Create Posts</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>Manage Units</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>School Access</th>
                            <th style={{ padding: '12px', border: '1px solid #555', textAlign: 'center', color: 'white' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {userRoles.map((role) => (
                            <tr key={role.id} style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                <td style={{ padding: '8px', border: '1px solid #555', color: 'white' }}>
                                    <input
                                        type="email"
                                        value={role.email}
                                        onChange={(e) => role.id && debouncedUpdate(role.id, { email: e.target.value })}
                                        style={{
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            border: '1px solid #555',
                                            color: 'white',
                                            padding: '4px',
                                            borderRadius: '3px',
                                            width: '100%'
                                        }}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', color: 'white' }}>
                                    <input
                                        type="text"
                                        value={role.displayName}
                                        onChange={(e) => role.id && debouncedUpdate(role.id, { displayName: e.target.value })}
                                        style={{
                                            backgroundColor: 'rgba(0,0,0,0.5)',
                                            border: '1px solid #555',
                                            color: 'white',
                                            padding: '4px',
                                            borderRadius: '3px',
                                            width: '100%'
                                        }}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', color: 'white' }}>
                                    <span style={{ fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }}>
                                        {role.userId}
                                    </span>
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={role.isAdmin}
                                        onChange={(e) => role.id && updateUserRole(role.id, { isAdmin: e.target.checked }, true)}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={role.canManageCourses}
                                        onChange={(e) => role.id && updateUserRole(role.id, { canManageCourses: e.target.checked }, true)}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={role.canViewPosts}
                                        onChange={(e) => role.id && updateUserRole(role.id, { canViewPosts: e.target.checked }, true)}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={role.canCreatePosts}
                                        onChange={(e) => role.id && updateUserRole(role.id, { canCreatePosts: e.target.checked }, true)}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={role.canManageUnits}
                                        onChange={(e) => role.id && updateUserRole(role.id, { canManageUnits: e.target.checked }, true)}
                                    />
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                        {schools.map(school => (
                                            <label key={school.id} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', color: 'white' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={role.allowedSchools?.includes(school.id) || false}
                                                    onChange={() => toggleSchoolAccess(role, school.id)}
                                                />
                                                {school.name}
                                            </label>
                                        ))}
                                    </div>
                                </td>
                                <td style={{ padding: '8px', border: '1px solid #555', textAlign: 'center' }}>
                                    <button
                                        onClick={() => role.id && deleteUserRole(role.id)}
                                        style={{ 
                                            padding: '4px 8px', 
                                            backgroundColor: '#dc3545', 
                                            color: 'white', 
                                            border: 'none', 
                                            borderRadius: '3px', 
                                            fontSize: '11px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '30px', padding: '20px', backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '8px', border: '1px solid #555' }}>
                <h3 style={{ color: 'white' }}>Instructions:</h3>
                <ul style={{ color: 'white' }}>
                    <li><strong>Search by UID:</strong> Enter user's UID to create permissions</li>
                    <li><strong>Text Fields:</strong> Email and display name update automatically after you stop typing (no alerts)</li>
                    <li><strong>Admin:</strong> Full access to everything</li>
                    <li><strong>Manage Courses:</strong> Can create/edit courses for allowed schools</li>
                    <li><strong>View/Create Posts:</strong> Controls access to posts functionality</li>
                    <li><strong>Manage Units:</strong> Can create new units when posting</li>
                    <li><strong>School Access:</strong> For course managers - which schools they can manage</li>
                </ul>
                <p style={{ marginTop: '15px', color: 'white' }}>
                    <strong>Getting UIDs:</strong> Send users to /get-uid page to copy their Firebase UID.
                </p>
            </div>
        </div>
    );
};